import { AnyAction } from 'redux';
import { handleActions } from 'redux-actions';

import {
  getPostsAction,
  getPostAction,
  createPostAction,
  updatePostAction,
  deletePostAction,
  getCommentsAction,
  createCommentAction,
  updateCommentAction,
  deleteCommentAction,
  getCommunityTopicsAction,

  setPostListReactionAction,
  setDeletePostListReactionAction,
  setPostReactionAction,
  setDeletePostReactionAction,
  setCommentReactionAction,
  setDeleteCommentReactionAction,

  clearPostAction,
  clearCommentsAction,
} from './CommunityBoardActionConfig';

// reducers
const defaultState = {
  posts: [],
  post: {},
  meta: {
    current_page: 1,
    last_page: 1,
    total: 0,
  },
  comments: {
    data: [],
    links: {},
    meta: {
      current_page: 1,
      last_page: 1,
      total: 0,
      per_page: 10,
    },
  },
  topics: [],
};

type State = typeof defaultState;

const reducer = handleActions<State, AnyAction>({
  [`${getPostsAction}`](state, { payload }: AnyAction) {
    return {
      ...state,
      posts: payload?.data,
      meta: payload?.meta,
    };
  },
  [`${getPostAction}`](state, { payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): posts/{id} show now answers {data: <post>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    return {
      ...state,
      post: payload,
    };
  },
  [`${getCommunityTopicsAction}`](state, { payload: rawPayload, params }: AnyAction) {
    if (params?.forDropdownSelect) return state;
    // Phase 3 (backend): post/topics now answers {data: [...]} instead of the array directly.
    const payload = rawPayload?.data ?? rawPayload;
    return {
      ...state,
      topics: payload,
    };
  },
  [`${createPostAction}`](state, { params }: AnyAction) {
    const newTopics = [ ...state.topics ];
    const updatedTopics = newTopics.map((topic: any) => {
      // Check all topics in params and filter only matching ones
      const matchingTopics = params.topic.filter((t: any) => t.value === topic.id
        || t.parentId === topic.id);

      if (matchingTopics.length > 0) {
        const updatedSubTopics = topic.sub_topics?.map((sub: any) => {
          const matchingSub = params.topic.find((t: any) => t.value === sub.id);
          if (matchingSub) {
            return {
              ...sub,
              count: sub.count + 1,
            };
          }
          return sub;
        });

        return {
          ...topic,
          count: topic.count + 1,
          sub_topics: updatedSubTopics,
        };
      }

      return topic;
    });
    return {
      ...state,
      topics: updatedTopics,
    };
  },
  [`${deletePostAction}`](state, { params }: AnyAction) {
    const newTopics = [ ...state.topics ];
    const updatedTopics = newTopics.map((topic: any) => {
      // Check all topics in params and filter only matching ones
      const matchingTopics = params.topics.filter((t: any) => t?.details?.id === topic.id
        || t?.details?.parentId === topic.id);

      if (matchingTopics.length > 0) {
        const updatedSubTopics = topic.sub_topics?.map((sub: any) => {
          const matchingSub = params.topics.find((t: any) => t?.details?.id === sub.id);
          if (matchingSub) {
            return {
              ...sub,
              count: sub.count - 1,
            };
          }
          return sub;
        });

        return {
          ...topic,
          count: topic.count - 1,
          sub_topics: updatedSubTopics,
        };
      }

      return topic;
    });
    return {
      ...state,
      topics: updatedTopics,
    };
  },
  [`${updatePostAction}`](state, { payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): posts/{id} update now answers {data: <post>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    const newPosts: any = [ ...state.posts ];
    const newPost: any = { ...state.post };
    // if new post has a value, update the viewed post
    if (newPost?.id) {
      const updatedPost = {
        ...newPost,
        title: payload?.title,
        content: payload?.content,
        is_anonymous: payload?.is_anonymous,
        topics: payload?.topics,
      };

      return {
        ...state,
        post: updatedPost,
      };
    }

    const updatedPosts = newPosts.map((post: any) => {
      if (post.id !== payload.id) return post;

      let updatedPost = { ...post };

      updatedPost = {
        ...updatedPost,
        title: payload?.title,
        content: payload?.content,
        is_anonymous: payload?.is_anonymous,
        topics: payload?.topics,
      };

      return updatedPost;
    });

    return {
      ...state,
      posts: updatedPosts,
    };
  },
  [`${setPostListReactionAction}`](state, { payload }: AnyAction) {
    const { post_id: postId, reaction } = payload;
    const newPosts = [ ...state.posts ];
    const updatedPosts = newPosts.map((post: any) => {
      if (post.id !== postId) return post;

      let updatedPost = { ...post };

      // up vote reaction
      if (reaction === 'upvote') {
        if (post.is_downvoted) {
          updatedPost = {
            ...updatedPost,
            upvotes_count: updatedPost.upvotes_count + 1,
            is_upvoted: true,
            downvotes_count: updatedPost.downvotes_count - 1,
            is_downvoted: false,
          };
          return updatedPost;
        }

        updatedPost = {
          ...updatedPost,
          upvotes_count: updatedPost.upvotes_count + 1,
          is_upvoted: true,
        };

        return updatedPost;
      }

      // down vote reaction
      if (post.is_upvoted) {
        updatedPost = {
          ...updatedPost,
          upvotes_count: updatedPost.upvotes_count - 1,
          is_upvoted: false,
          downvotes_count: updatedPost.downvotes_count + 1,
          is_downvoted: true,
        };

        return updatedPost;
      }

      updatedPost = {
        ...updatedPost,
        downvotes_count: updatedPost.downvotes_count + 1,
        is_downvoted: true,
      };

      return updatedPost;
    });

    return {
      ...state,
      posts: updatedPosts,
    };
  },
  [`${setDeletePostListReactionAction}`](state, { payload }: AnyAction) {
    const { post_id: postId, reaction } = payload;
    const newPosts = [ ...state.posts ];
    const updatedPosts = newPosts.map((post: any) => {
      if (post.id !== postId) return post;

      let updatedPost = { ...post };

      // up vote reaction
      if (reaction === 'upvote') {
        updatedPost = {
          ...updatedPost,
          upvotes_count: updatedPost.upvotes_count - 1,
          is_upvoted: false,
        };

        return updatedPost;
      }

      // down vote reaction
      updatedPost = {
        ...updatedPost,
        downvotes_count: updatedPost.downvotes_count - 1,
        is_downvoted: false,
      };

      return updatedPost;
    });

    return {
      ...state,
      posts: updatedPosts,
    };
  },
  [`${setPostReactionAction}`](state, { payload }: AnyAction) {
    const { post_id: postId, reaction } = payload;
    let newPost: any = { ...state.post };

    if (newPost.id !== postId) return { ...state, post: newPost };

    // up vote reaction
    if (reaction === 'upvote') {
      if (newPost.is_downvoted) {
        newPost = {
          ...newPost,
          upvotes_count: newPost.upvotes_count + 1,
          is_upvoted: true,
          downvotes_count: newPost.downvotes_count - 1,
          is_downvoted: false,
        };
        return {
          ...state,
          post: newPost,
        };
      }

      newPost = {
        ...newPost,
        upvotes_count: newPost.upvotes_count + 1,
        is_upvoted: true,
      };

      return {
        ...state,
        post: newPost,
      };
    }

    // down vote reaction
    if (newPost.is_upvoted) {
      newPost = {
        ...newPost,
        upvotes_count: newPost.upvotes_count - 1,
        is_upvoted: false,
        downvotes_count: newPost.downvotes_count + 1,
        is_downvoted: true,
      };

      return {
        ...state,
        post: newPost,
      };
    }

    newPost = {
      ...newPost,
      downvotes_count: newPost.downvotes_count + 1,
      is_downvoted: true,
    };

    return {
      ...state,
      post: newPost,
    };
  },
  [`${setDeletePostReactionAction}`](state, { payload }: AnyAction) {
    const { post_id: postId, reaction } = payload;
    let newPost: any = { ...state.post };

    if (newPost.id !== postId) return { ...state, post: newPost };

    // up vote reaction
    if (reaction === 'upvote') {
      newPost = {
        ...newPost,
        upvotes_count: newPost.upvotes_count - 1,
        is_upvoted: false,
      };

      return {
        ...state,
        post: newPost,
      };
    }

    // down vote reaction
    newPost = {
      ...newPost,
      downvotes_count: newPost.downvotes_count - 1,
      is_downvoted: false,
    };

    return {
      ...state,
      post: newPost,
    };
  },
  [`${getCommentsAction}`](state, { params, payload }: AnyAction) {
    if (params?.parent_id) {
      const newComments = [ ...state.comments.data ];

      const updatedComments = newComments.map((comment: any) => {
        if (comment.id === params.parent_id) {
          // Check if replies exist and concat the new data
          const existingReplies = comment.replies || { data: [], meta: {}, links: {} };
          const filteredNewReplies = payload.data.filter(
            (newReply: any) => !existingReplies.data.some((reply: any) => reply.id === newReply.id),
          );
          return {
            ...comment,
            replies: {
              data: [ ...existingReplies.data, ...filteredNewReplies ],
              meta: payload.meta,
              links: payload.links,
            },
          };
        }
        return comment;
      });

      return {
        ...state,
        comments: {
          ...state.comments,
          data: updatedComments,
        },
      };
    }

    if (params?.load_more) {
      // Filter out duplicate comments
      const filteredNewComments = payload.data.filter(
        (newComment: any) => !state.comments.data
          .some((existingComment: any) => existingComment.id === newComment.id),
      );
      return {
        ...state,
        comments: {
          ...state.comments,
          data: [ ...state.comments.data, ...filteredNewComments ],
          meta: payload.meta,
          links: payload.links,
        },
      };
    }

    const filteredPayload = payload.data.filter(
      (newComment: any) => !state.comments.data
        .some((existingComment: any) => existingComment.id === newComment.id),
    );

    return {
      ...state,
      // comments: payload,
      comments: {
        ...payload,
        data: filteredPayload,
      },
    };
  },
  [`${createCommentAction}`](state, { params, payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): comments store now answers {data: <comment>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    const newComments: any = { ...state.comments };
    const perPageLimit = newComments.meta.per_page;
    const newCommentsMeta: any = { ...state.comments.meta };
    const newPost: any = { ...state.post };
    if (params?.post_id === newPost.id) {
      newPost.comments_count += 1;
    }

    if (params?.parent_id) {
      const newCommentsData = [ ...newComments.data ];
      const updatedComments = newCommentsData.map((comment: any) => {
        if (comment.id === params.parent_id) {
          const existingReplies = comment.replies || { data: [], meta: {}, links: {} };
          const repliesPerPageLimit = existingReplies?.meta?.per_page;
          const combinedReplies = [ payload, ...existingReplies.data ];
          if (repliesPerPageLimit && repliesPerPageLimit > 0
            && combinedReplies.length > repliesPerPageLimit) {
            // remove the last reply if the limit reaches and a new one is added
            combinedReplies.pop();
          }
          return {
            ...comment,
            replies_count: comment.replies_count + 1,
            replies: {
              ...existingReplies,
              data: combinedReplies,
            },
          };
        }
        return comment;
      });

      return {
        ...state,
        post: newPost,
        comments: {
          ...state.comments,
          data: updatedComments,
          meta: newCommentsMeta,
        },
      };
    }
    const commentData = [ ...newComments.data ];
    // add payload to the first index
    commentData.unshift(payload);
    newCommentsMeta.total += 1;

    if (commentData.length > perPageLimit) {
      // remove the last comment if the limit reaches and a new one is added
      commentData.pop();
    }

    return {
      ...state,
      post: newPost,
      comments: {
        ...newComments,
        data: commentData,
        meta: newCommentsMeta,
      },
    };
  },
  [`${updateCommentAction}`](state, { params, payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): comments update now answers {data: <comment>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    const newComments: any = { ...state.comments };
    const parentId = params?.parent_id;
    const commentId = params?.comment_id;

    const updateCommentData = (comment: any) => ({
      ...comment,
      message: payload?.message,
      is_anonymous: payload?.is_anonymous,
      user: payload?.user,
    });

    const updatedCommentsData = newComments.data.map((comment: any) => {
      const updatedComment = { ...comment };

      // if parent id is present, it means it's a reply
      if (parentId) {
        if (comment.id === parentId) {
          updatedComment.replies = {
            ...updatedComment.replies,
            data: updatedComment.replies.data
              .map((reply: any) => (reply.id === commentId ? updateCommentData(reply) : reply)),
          };
        }
        return updatedComment;
      }

      return comment.id === payload.id ? updateCommentData(comment) : comment;
    });
    return {
      ...state,
      comments: {
        ...newComments,
        data: updatedCommentsData,
      },
    };
  },
  [`${deleteCommentAction}`](state, { params }: AnyAction) {
    const newComments: any = { ...state.comments };
    const newCommentsMeta: any = { ...state.comments.meta };
    const newPost: any = { ...state.post };
    if (params?.post_id === newPost.id) {
      newPost.comments_count -= 1;
    }

    if (params?.parent_id) {
      const updatedComments = newComments.data.map((comment: any) => {
        if (comment.id === params?.parent_id) {
          const existingReplies = comment.replies || { data: [], meta: {}, links: {} };
          return {
            ...comment,
            replies_count: comment.replies_count - 1,
            replies: {
              ...comment.replies,
              data: existingReplies?.data.filter((reply: any) => reply.id !== params?.comment_id),
              meta: {
                ...existingReplies?.meta,
                current_page: existingReplies.meta.current_page > 0
                  ? existingReplies.meta.current_page - 1 : existingReplies.meta.current_page,
              },
            },
          };
        }
        return comment;
      });

      return {
        ...state,
        post: newPost,
        comments: {
          ...newComments,
          data: updatedComments,
          meta: newCommentsMeta,
        },
      };
    }
    const updatedComments = newComments.data
      .filter((comment: any) => comment.id !== params?.comment_id);

    newCommentsMeta.total -= 1;
    if (newCommentsMeta.current_page > 1) {
      newCommentsMeta.current_page -= 1;
    }

    return {
      ...state,
      post: newPost,
      comments: {
        ...newComments,
        data: updatedComments,
        meta: newCommentsMeta,
      },
    };
  },
  [`${setCommentReactionAction}`](state, { payload }: AnyAction) {
    const updateReaction = (comment: any, reaction: string) => {
      let updatedComment = { ...comment };

      // up vote reaction
      if (reaction === 'upvote') {
        if (updatedComment.is_downvoted) {
          updatedComment = {
            ...updatedComment,
            upvotes_count: updatedComment.upvotes_count + 1,
            is_upvoted: true,
            downvotes_count: updatedComment.downvotes_count - 1,
            is_downvoted: false,
          };
          return updatedComment;
        }

        updatedComment = {
          ...updatedComment,
          upvotes_count: updatedComment.upvotes_count + 1,
          is_upvoted: true,
        };

        return updatedComment;
      }

      // down vote reaction
      if (updatedComment.is_upvoted) {
        updatedComment = {
          ...updatedComment,
          upvotes_count: updatedComment.upvotes_count - 1,
          is_upvoted: false,
          downvotes_count: updatedComment.downvotes_count + 1,
          is_downvoted: true,
        };

        return updatedComment;
      }
      updatedComment = {
        ...updatedComment,
        downvotes_count: updatedComment.downvotes_count + 1,
        is_downvoted: true,
      };

      return updatedComment;
    };

    const newComments = [ ...state.comments.data ];
    const reaction = payload?.reaction;
    const parentId = payload?.parent_id;
    const commentId = payload?.comment_id;

    const updatedComments = newComments.map((comment: any) => {
      const updatedComment = { ...comment };
      // if the comment is a reply, find the parent and update the reaction
      if (parentId) {
        if (comment.id === parentId) {
          updatedComment.replies = {
            ...updatedComment.replies,
            data: updatedComment.replies.data.map((reply: any) => {
              if (reply.id === commentId) {
                return updateReaction(reply, reaction);
              }
              return reply;
            }),
          };
        }
        return updatedComment;
      }

      // If there is no parent_id, it's a top-level comment
      if (comment.id === commentId) {
        return updateReaction(updatedComment, reaction);
      }

      return updatedComment;
    });

    return {
      ...state,
      comments: {
        ...newComments,
        data: updatedComments,
      },
    };
  },
  [`${setDeleteCommentReactionAction}`](state, { payload }: AnyAction) {
    const removeReaction = (comment: any, reaction: string) => {
      let updatedComment = { ...comment };

      // Remove the reaction logic for upvote and downvote
      if (reaction === 'upvote') {
        updatedComment = {
          ...updatedComment,
          upvotes_count: updatedComment.upvotes_count - 1,
          is_upvoted: false,
        };

        return updatedComment;
      }

      updatedComment = {
        ...updatedComment,
        downvotes_count: updatedComment.downvotes_count - 1,
        is_downvoted: false,
      };

      return updatedComment;
    };
    const newComments = [ ...state.comments.data ];
    const reaction = payload?.reaction;
    const parentId = payload?.parent_id;
    const commentId = payload?.comment_id;
    const updatedComments = newComments.map((comment: any) => {
      const updatedComment = { ...comment };
      if (parentId) {
        if (comment.id === parentId) {
          updatedComment.replies = {
            ...updatedComment.replies,
            data: updatedComment.replies.data.map((reply: any) => {
              if (reply.id === commentId) {
                return removeReaction(reply, reaction);
              }
              return reply;
            }),
          };
        }
        return updatedComment;
      }

      // If there is no parent_id, it's a top-level comment
      if (comment.id === commentId) {
        return removeReaction(updatedComment, reaction);
      }

      return updatedComment;
    });

    return {
      ...state,
      comments: {
        ...state.comments,
        data: updatedComments,
      },
    };
  },
  [`${clearPostAction}`](state) {
    return {
      ...state,
      post: {},
    };
  },
  [`${clearCommentsAction}`](state) {
    return {
      ...state,
      comments: {
        data: [],
        links: {},
        meta: {},
      },
    };
  },
}, defaultState);

export default reducer;
